// analytics/tabs/SessionsTrendsTab.jsx - Updated with unified filters

import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
// import inferenceService from '../../../js/Inspection/services/inferenceService.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';
import { TrendChart } from '../components/charts/TrendChart.jsx';

// Use the unified filter hook and components
import { useTabFilters } from '../hooks/useTabFilters.js';
import { UnifiedFilterPanel } from '../components/filters/UnifiedFilterComponents.jsx';

// Import the reusable inspection components
import {
    InspectionTable,
    InspectionDetailsModal,
    ImageModal,
} from '../components/InspectionComponents.jsx';

import {
    SessionCard,
    SessionDetailsModal
} from '../components/SessionComponents.jsx';

export const SessionsTrendsTab = () => {
    const { state, setError, addNotification } = useAppContext();
    const { itemsPerPage, includeManualImages } = state.preferences;

    // State for pagination
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    
    // Use unified filters with session-specific defaults
    const {
        filters,
        handleFilterChange,
        resetFilters,
        getConfiguredFilters,
        hasInitialized
    } = useTabFilters(
        {
            sortBy: 'start_time' // Session-specific default
        },
        {
            onPageReset: () => setPagination(prev => ({ ...prev, page: 1 }))
        }
    );

    // State for filter dropdown options
    const [availableOptions, setAvailableOptions] = useState({ 
        projects: [], 
        lotIds: [], 
        stations: [] 
    });
    
    // State for the main list of sessions
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // State for the session details modal
    const [showDetails, setShowDetails] = useState(false);
    const [currentSession, setCurrentSession] = useState(null);
    const [trendData, setTrendData] = useState([]);
    const [isTrendLoading, setIsTrendLoading] = useState(false);

    // State for inspections within the modal and their pagination
    const [sessionInspections, setSessionInspections] = useState([]);
    const [isInspectionsLoading, setIsInspectionsLoading] = useState(false);
    const [inspPagination, setInspPagination] = useState({ 
        page: 1, 
        limit: itemsPerPage || 10, 
        total: 0, 
        totalPages: 1 
    });

    // State for nested inspection components (details modal, image viewer)
    const [modelList, setModelList] = useState([]);
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [showInspectionDetails, setShowInspectionDetails] = useState(false);
    const [largeImage, setLargeImage] = useState(null);
    const [isGroundTruthLoading, setIsGroundTruthLoading] = useState(false);

    // --- Configuration Sync Effect ---
    useEffect(() => {
        setInspPagination(prev => ({
            ...prev,
            limit: itemsPerPage || 10
        }));
    }, [itemsPerPage]);

    // --- API Loaders ---

    // Fetch master data (projects, stations, models) on initial mount
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [projectsRes, stationsRes, modelsRes] = await Promise.all([
                    api.getProjects(),
                    api.getStations(),
                    // inferenceService.fetchAvailableModels(null)
                    api.getAvailableModels(null)
                ]);
                setAvailableOptions(prev => ({
                    ...prev,
                    projects: Array.isArray(projectsRes) ? projectsRes.map(p => p.name) : [],
                    stations: Array.isArray(stationsRes) ? stationsRes : [],
                }));
                setModelList(modelsRes || []);
            } catch (err) {
                addNotification?.({ type: 'danger', message: 'Failed to load page data.' });
            }
        };
        
        loadInitialData();
    }, [addNotification]);

    // Update lot ID options when the project filter changes
    useEffect(() => {
        if (!filters.project) {
            setAvailableOptions(prev => ({ ...prev, lotIds: [] }));
            return;
        }
        api.getLotIdentifiers(filters.project)
            .then(lotIds => {
                setAvailableOptions(prev => ({ ...prev, lotIds: lotIds || [] }));
            })
            .catch(err => console.error('[SessionsTrendsTab] getLotIdentifiers failed', err));
    }, [filters.project]);

    // --- Data Fetching Callbacks ---

    const loadSessions = useCallback(async (page = 1) => {
        if (!hasInitialized) return;

        setIsLoading(true);
        setError(null);
        try {
            const configuredFilters = getConfiguredFilters();
            const res = await api.getSessions(configuredFilters, page, itemsPerPage || 10);
            setSessions(res.data || []);
            setPagination({ 
                page: res.page || page, 
                totalPages: res.totalPages || 1, 
                total: res.total || 0 
            });
        } catch (err) {
            setError(err);
            setSessions([]);
        } finally {
            setIsLoading(false);
        }
    }, [filters, setError, itemsPerPage, getConfiguredFilters, hasInitialized]);

    // Reload sessions when filters or page number change
    useEffect(() => {
        loadSessions(pagination.page);
    }, [loadSessions, pagination.page]);

    // --- Event Handlers ---

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.page) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    const refreshSessions = () => loadSessions(1);

    // --- Modal Handlers ---

    const loadModalInspections = useCallback(async (sessionId, page = 1, limit) => {
        setIsInspectionsLoading(true);
        try {
            const configuredFilters = getConfiguredFilters();
            const inspectionsFilter = {
                sessionId,
                ...configuredFilters,
                sortBy: 'inspection_time',
                sortDirection: 'desc'
            };
            
            const res = await api.getSessionInspections(sessionId, page, limit || itemsPerPage || 10);
            setSessionInspections(res.data || []);
            setInspPagination({ 
                page: res.page || page, 
                limit: res.limit || limit, 
                total: res.total || 0, 
                totalPages: res.total_pages || 1 
            });
        } catch (err) {
            addNotification?.({ type: 'danger', message: `Failed loading inspections: ${err.message}` });
        } finally {
            setIsInspectionsLoading(false);
        }
    }, [addNotification, itemsPerPage, getConfiguredFilters]);

    const handleViewDetails = useCallback(async (session) => {
        const sessionId = session.id || session._id;
        if (!sessionId) return;

        setCurrentSession({ ...session, id: sessionId });
        setShowDetails(true);
        setTrendData([]);
        setIsTrendLoading(true);

        loadModalInspections(sessionId, 1, inspPagination.limit);

        try {
            const trendResult = await api.getSessionTrend(sessionId, filters.dateRange);
            setTrendData(trendResult || []);
        } catch (err) {
            addNotification?.({ type: 'warning', message: `Could not load trend data: ${err.message}` });
        } finally {
            setIsTrendLoading(false);
        }
    }, [addNotification, loadModalInspections, filters.dateRange, inspPagination.limit]);

    const closeModal = () => {
        setShowDetails(false);
        setCurrentSession(null);
        setTrendData([]);
        setSessionInspections([]);
        setInspPagination({ page: 1, limit: itemsPerPage || 10, total: 0, totalPages: 1 });
    };

    const changeInspPage = (newPage) => {
        if (currentSession && newPage >= 1 && newPage <= inspPagination.totalPages) {
            loadModalInspections(currentSession.id, newPage, inspPagination.limit);
        }
    };

    // --- Nested Modal Handlers ---

    const viewInspectionDetails = (insp) => {
        setSelectedInspection(insp);
        setShowInspectionDetails(true);
    };

    const closeInspectionDetails = () => {
        setSelectedInspection(null);
        setShowInspectionDetails(false);
    };

    const handleImageClick = (url) => setLargeImage(url);
    const closeLargeImage = () => setLargeImage(null);

    const setGroundTruth = async (inspectionId, status) => {
        setIsGroundTruthLoading(true);
        try {
            const data = await api.confirmClassification(inspectionId, status);
            const update = { 
                confirmation_status: status, 
                confirmed_by: data?.username || 'user', 
                confirmation_timestamp: new Date().toISOString() 
            };
            
            setSessionInspections(prev => prev.map(i => (i._id === inspectionId ? { ...i, ...update } : i)));
            if (selectedInspection?._id === inspectionId) {
                setSelectedInspection(prev => ({ ...prev, ...update }));
            }
            
            addNotification?.({ type: 'success', message: 'Ground truth updated.' });
        } catch (err) {
            addNotification?.({ type: 'danger', message: `Ground truth update failed: ${err.message}` });
        } finally {
            setIsGroundTruthLoading(false);
        }
    };

    // --- Render ---

    return (
        <ErrorBoundary error={state.error} onRetry={() => loadSessions(1)}>
            <div className="sessions-content fade-in">
                <div className="mb-4">
                    <h4 className="mb-1">Session Performance & Trends</h4>
                    <p className="text-muted mb-0">Review and analyze data from individual inspection sessions.</p>
                </div>

                <UnifiedFilterPanel 
                    filters={filters} 
                    onFilterChange={handleFilterChange}
                    availableOptions={availableOptions}
                    config={{ includeManualImages, itemsPerPage }}
                    tabType="sessions"
                    showConfigInfo={true}
                />

                <div className="card shadow-sm mb-3">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fas fa-history text-primary me-2" />Inspection Sessions
                        </h5>
                        <div className="d-flex align-items-center gap-3">
                            <small className="text-muted">
                                Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} total)
                            </small>
                            {/* <button 
                                className="btn btn-sm btn-outline-primary" 
                                onClick={refreshSessions} 
                                disabled={isLoading} 
                                title="Refresh sessions"
                            >
                                <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`} />
                            </button> */}
                        </div>
                    </div>
                </div>

                {!hasInitialized ? (
                    <LoadingSpinner message="Initializing filters..." size="large" />
                ) : isLoading ? (
                    <LoadingSpinner message="Loading sessions..." size="large" />
                ) : sessions.length > 0 ? (
                    <>
                        <div className="sessions-list">
                            {sessions.map((s) => (
                                <SessionCard key={s.id} session={s} onViewDetails={handleViewDetails} />
                            ))}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="card shadow-sm mt-3">
                                <div className="card-body">
                                    <nav className="d-flex justify-content-center">
                                        <ul className="pagination mb-0">
                                            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(1)}>«</button>
                                            </li>
                                            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(pagination.page - 1)}>‹ Prev</button>
                                            </li>
                                            <li className="page-item active">
                                                <span className="page-link">{pagination.page}</span>
                                            </li>
                                            <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(pagination.page + 1)}>Next ›</button>
                                            </li>
                                            <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(pagination.totalPages)}>»</button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="card shadow-sm">
                        <div className="card-body text-center p-5">
                            <i className="fas fa-history fa-4x text-muted mb-4" />
                            <h4 className="text-muted">No Sessions Found</h4>
                            <p className="text-muted">No sessions match the current filters.</p>
                            <button 
                                className="btn btn-primary mt-3" 
                                onClick={resetFilters}
                                title="Reset all filters to defaults"
                            >
                                <i className="fas fa-undo me-2"></i>Reset Filters
                            </button>
                        </div>
                    </div>
                )}

                {/* All Modals */}
                <SessionDetailsModal
                    session={currentSession}
                    isOpen={showDetails}
                    onClose={closeModal}
                    trendData={trendData}
                    isTrendLoading={isTrendLoading}
                    sessionInspections={sessionInspections}
                    isInspectionsLoading={isInspectionsLoading}
                    onViewInspectionDetails={viewInspectionDetails}
                    onImageClick={handleImageClick}
                    onSetGroundTruth={setGroundTruth}
                    isGroundTruthLoading={isGroundTruthLoading}
                    inspectionPagination={inspPagination}
                    onInspectionPageChange={changeInspPage}
                    addNotification={addNotification}
                    modelList={modelList}
                />

                {showInspectionDetails && selectedInspection && (
                    <InspectionDetailsModal
                        inspection={selectedInspection}
                        modelList={modelList}
                        onClose={closeInspectionDetails}
                        onImageClick={handleImageClick}
                        onSetGroundTruth={setGroundTruth}
                        isLoading={isGroundTruthLoading}
                        addNotification={addNotification}
                    />
                )}
                
                <ImageModal 
                    src={largeImage} 
                    alt="Enlarged view" 
                    show={!!largeImage} 
                    onClose={closeLargeImage} 
                />
            </div>
        </ErrorBoundary>
    );
};
